/**
 * 背景（情景）シーン。
 *
 * ねらい: 「屋台に座って博多の夜景を見上げている」感覚を出すこと。
 * ゲームロジックは一切持たず、GameScene の下に重ねて描画するだけの飾り。
 *
 * 3層構成
 *   遠景 : 空・月・博多湾・街のシルエット（ゆっくり視差移動）
 *   中景 : 屋台の梁・提灯・のぼり（揺れる／時間帯で点灯）
 *   近景 : カウンターの木目・湯呑み・ゆらぐ湯気
 *
 * 到達レベルが上がると夕暮れ→薄暮→夜へ移り、街の灯りが増える。
 * 「進んでいる感」を背景で伝えることで離脱を防ぐのが目的。
 *
 * 追加: 背景ツアー「福岡めぐり」が有効な場合、時間経過で写真背景に切り替わり、
 * ホットスポットマーカーと地名看板を表示する。
 */

import Phaser from 'phaser';

import { GAME_TUNING } from '../config/gameConfig';
import { isMotionReduced } from '../effects/shakePolicy';
import type { ScenePhase, TourHotspot, TourScene } from '../config/types';
import type { GameContext } from '../GameContext';
import { sceneryPhotoKey, tourPhotoKey } from '../utils/assetLoader';
import {
  computeTourState,
  findHitHotspot,
  sceneIndexAt,
  tourHotspotWorldPos,
} from '../utils/tour';
import {
  CITY_LIGHTS_TEXTURE_KEY,
  GLOW_TEXTURE_KEY,
  SCENE_PHASES,
  SCENERY_LAYOUT,
  SKYLINE_TEXTURE_KEY,
  STEAM_TEXTURE_KEY,
  VIGNETTE_TEXTURE_KEY,
  createCityLightsTexture,
  createSkyTexture,
  createSkylineTexture,
  createVignetteTexture,
  lightIntensityOf,
  phaseIndex,
  skyTextureKey,
} from '../utils/sceneryTextures';

/** 盤面の中の案内（旧）。いまは TourChip の吹き出しを使う。 */
const SHOW_CANVAS_TOUR_HINT = false as boolean;

export const BACKGROUND_SCENE_KEY = 'BackgroundScene';

const PHASES: readonly ScenePhase[] = SCENE_PHASES;

export class BackgroundScene extends Phaser.Scene {
  private context!: GameContext;

  private phase: ScenePhase = 'dusk';
  private readonly skies = new Map<ScenePhase, Phaser.GameObjects.Image>();
  private skyContainer!: Phaser.GameObjects.Container;
  /** 実写写真に置き換わった時間帯。手続き生成の遠景を出すかどうかの判断に使う。 */
  private readonly photoPhases = new Set<ScenePhase>();
  private readonly lanternGlows: Phaser.GameObjects.Image[] = [];
  private stars!: Phaser.GameObjects.Container;
  private farView!: Phaser.GameObjects.Container;
  private bay!: Phaser.GameObjects.Container;
  private cityLights!: Phaser.GameObjects.Container;
  private sparkles!: Phaser.GameObjects.Container;
  private embers!: Phaser.GameObjects.Container;

  /** 空の演出（花火・流れ星）を描き足す場所。フェーズで出番が変わる。 */
  private skyFx!: Phaser.GameObjects.Container;
  private skyFxBounds = { left: 0, top: 0, width: 0, horizon: 0 };

  /** 屋台の飾り（梁・提灯・のぼり）。ツアー有効時は非表示にできる。 */
  private stall!: Phaser.GameObjects.Container;
  private counter!: Phaser.GameObjects.Graphics;
  private ambientSteam!: Phaser.GameObjects.Container;
  private vignette!: Phaser.GameObjects.Image;

  /** 背景ツアー用レイヤー。 */
  private tourLayer?: Phaser.GameObjects.Container;
  private tourImages = new Map<string, Phaser.GameObjects.Image>();
  private tourMarkers = new Map<string, Phaser.GameObjects.Container>();
  private tourSignboard?: Phaser.GameObjects.Container;
  private currentTourSceneId?: string;
  /** いま映っている景色が「静かに扱うべき景色」か（追悼・慰霊・聖域）。 */
  private solemnScene = false;
  private tourElapsedMs = 0;
  private tourAvailable = false;
  /** 初見向けヒントの吹き出し。 */
  private tourHint?: Phaser.GameObjects.Container;
  private tourHintShown = false;
  private tourHintScheduled = false;

  private unsubscribe: (() => void) | null = null;

  constructor() {
    super({ key: BACKGROUND_SCENE_KEY });
  }

  preload(): void {
    const context = this.registry.get('context') as GameContext | undefined;
    if (!context) return;

    // 背景ツアーの写真を先読みする（存在するものだけ）。
    const tour = context.stage.scenery?.tour;
    const tourPhotos = context.tourPhotos;
    if (tour && tourPhotos.size > 0) {
      tour.scenes.forEach((scene) => {
        if (tourPhotos.has(scene.id)) {
          this.load.image(tourPhotoKey(scene.id), scene.image);
        }
      });
    }
  }

  create(): void {
    this.context = this.registry.get('context') as GameContext;

    const { width, height } = GAME_TUNING.world;
    // 情景は画面全体に広げる（プレイエリアは器の形なので、外側にも景色を見せたい）
    const left = 0;
    const top = 0;
    const innerWidth = width;
    const innerHeight = height;

    this.buildTextures(innerWidth, innerHeight);

    const layer = this.add.container(0, 0);

    this.skyContainer = this.buildSky(left, top, innerWidth, innerHeight);
    layer.add(this.skyContainer);
    layer.add(this.buildStars(left, top, innerWidth));
    layer.add(this.buildSkyFx(left, top, innerWidth));
    this.farView = this.buildFarView(left, top);
    layer.add(this.farView);
    this.bay = this.buildBay(left, top, innerWidth);
    layer.add(this.bay);
    layer.add(this.buildWaterSparkles(left, top, innerWidth));
    this.stall = this.buildStall(left, top, innerWidth);
    layer.add(this.stall);
    this.counter = this.buildCounter(left, top, innerWidth, innerHeight);
    layer.add(this.counter);
    layer.add(this.buildEmbers(left, top, innerWidth, innerHeight));
    this.ambientSteam = this.buildAmbientSteam(left, top, innerWidth, innerHeight);
    layer.add(this.ambientSteam);
    this.vignette = this.buildVignette(left, top, innerWidth, innerHeight);
    layer.add(this.vignette);

    try {
      this.buildTourLayer(innerWidth, innerHeight);
    } catch (error) {
      console.warn('[BackgroundScene] 背景ツアーの構築に失敗しました。夜景のまま続行します。', error);
      this.tourAvailable = false;
    }

    this.applyPhase(this.phase, true);
    this.applyTourVisibility();

    this.unsubscribe = this.context.bus.on('scene:phase', ({ phase }) => this.applyPhase(phase));
    const unsubReset = this.context.bus.on('tour:reset', () => this.resetTourProgress());
    // 一度でも豆知識を開いたら、ヒントの役目は終わり
    const unsubDiscover = this.context.bus.on('tour:discover', () => {
      this.tourHintShown = true;
      this.hideTourHint();
    });
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      unsubReset();
      unsubDiscover();
      this.unsubscribe = null;
    });
  }

  private buildTextures(innerWidth: number, innerHeight: number): void {
    PHASES.forEach((phase) => createSkyTexture(this, phase, innerWidth, innerHeight));
    createSkylineTexture(this, innerWidth);
    createCityLightsTexture(this, innerWidth);
    createVignetteTexture(this, innerWidth, innerHeight);
  }

  private coverScale(textureKey: string, targetWidth: number, targetHeight: number): number {
    if (!this.textures.exists(textureKey)) return 1;
    try {
      const source = this.textures.get(textureKey).getSourceImage() as {
        width?: number;
        height?: number;
      };
      const w = source.width ?? 0;
      const h = source.height ?? 0;
      if (w <= 0 || h <= 0) return 1;
      return Math.max(targetWidth / w, targetHeight / h);
    } catch {
      return 1;
    }
  }

  /* --------------------------------- 遠景 --------------------------------- */

  private buildSky(
    left: number,
    top: number,
    innerWidth: number,
    innerHeight: number,
  ): Phaser.GameObjects.Container {
    // ケン・バーンズで動かすため、時間帯ごとの空をひとつの入れ物にまとめる
    const centerX = left + innerWidth / 2;
    const centerY = top + innerHeight / 2;
    const container = this.add.container(centerX, centerY);

    PHASES.forEach((phase) => {
      const photoKey = sceneryPhotoKey(phase);
      const usePhoto = this.textures.exists(photoKey);
      if (usePhoto) this.photoPhases.add(phase);
      const textureKey = usePhoto ? photoKey : skyTextureKey(phase);
      const scale = this.coverScale(textureKey, innerWidth, innerHeight);
      const image = this.add
        .image(0, 0, textureKey)
        .setOrigin(0.5, 0.5)
        .setScale(scale)
        .setAlpha(phase === this.phase ? 1 : 0);
      this.skies.set(phase, image);
      container.add(image);
    });

    const kb = GAME_TUNING.scenery.kenBurns;
    container.setScale(kb.minScale);
    container.setPosition(centerX - kb.driftXPx, centerY + kb.driftYPx);
    this.tweens.add({
      targets: container,
      scaleX: kb.maxScale,
      scaleY: kb.maxScale,
      x: centerX + kb.driftXPx,
      y: centerY - kb.driftYPx,
      duration: kb.durationMs,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    return container;
  }

  private buildStars(left: number, top: number, innerWidth: number): Phaser.GameObjects.Container {
    this.stars = this.add.container(left, top);
    for (let i = 0; i < 34; i += 1) {
      const x = ((i * 97) % (innerWidth - 24)) + 12;
      const y = 24 + ((i * 61) % (SCENERY_LAYOUT.horizonY - 90));
      const size = i % 7 === 0 ? 2.2 : 1.4;
      const star = this.add.circle(x, y, size, 0xfff6e2, 0.9);
      this.stars.add(star);
      this.tweens.add({
        targets: star,
        alpha: { from: 0.25, to: 0.95 },
        duration: 1200 + ((i * 137) % 1800),
        yoyo: true,
        repeat: -1,
        delay: (i * 83) % 1500,
      });
    }
    return this.stars;
  }

  private buildFarView(left: number, top: number): Phaser.GameObjects.Container {
    const baseY = top + SCENERY_LAYOUT.horizonY - SCENERY_LAYOUT.skylineHeight;
    const container = this.add.container(left, baseY);

    const skyline = this.add.image(0, 0, SKYLINE_TEXTURE_KEY).setOrigin(0, 0);

    // 窓明かりは「時間帯（親コンテナの alpha）×瞬き（子の alpha）」で二重に制御する
    this.cityLights = this.add.container(0, 0);
    const lights = this.add
      .image(0, 0, CITY_LIGHTS_TEXTURE_KEY)
      .setOrigin(0, 0)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.cityLights.add(lights);
    container.add([skyline, this.cityLights]);

    // ごくゆっくり左右に流れる視差（遠景ほど動きが小さい）
    const amplitude = GAME_TUNING.scenery.parallaxFarPx;
    container.setX(left - amplitude);
    this.tweens.add({
      targets: container,
      x: left + amplitude,
      duration: GAME_TUNING.scenery.parallaxDurationMs,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // 街の灯りは細かく瞬く
    this.tweens.add({
      targets: lights,
      alpha: { from: 0.72, to: 1 },
      duration: 2600,
      yoyo: true,
      repeat: -1,
    });

    return container;
  }

  private buildWaterSparkles(
    left: number,
    top: number,
    innerWidth: number,
  ): Phaser.GameObjects.Container {
    this.sparkles = this.add.container(left, top);
    const { horizonY, waterBottomY } = SCENERY_LAYOUT;
    for (let i = 0; i < 16; i += 1) {
      const y = horizonY + 6 + ((i * 29) % (waterBottomY - horizonY - 10));
      const w = 18 + ((i * 37) % 74);
      const x = ((i * 113) % (innerWidth - w - 16)) + 8;
      const bar = this.add
        .rectangle(x, y, w, 1.6, 0xffe9c4, 0.5)
        .setOrigin(0, 0.5)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.sparkles.add(bar);
      this.tweens.add({
        targets: bar,
        alpha: { from: 0.08, to: 0.5 },
        scaleX: { from: 0.7, to: 1.15 },
        duration: 1800 + ((i * 211) % 2200),
        yoyo: true,
        repeat: -1,
        delay: (i * 149) % 2000,
      });
    }
    return this.sparkles;
  }

  /* --------------------------------- 中景 --------------------------------- */

  /**
   * 博多湾のモチーフ（大鳥居と屋形船）。
   * 水面より下に置くことで「海を眺めている」奥行きを作る。
   */
  private buildBay(left: number, top: number, innerWidth: number): Phaser.GameObjects.Container {
    const container = this.add.container(left, top);
    const { horizonY, waterBottomY } = SCENERY_LAYOUT;

    // 大鳥居（水面に立つシルエット）
    const toriiX = innerWidth * 0.17;
    const baseY = horizonY + (waterBottomY - horizonY) * 0.52;
    const torii = this.add.graphics();
    torii.fillStyle(0x2a1220, 0.72);
    torii.fillRect(toriiX - 20, baseY - 34, 4, 34);
    torii.fillRect(toriiX + 16, baseY - 34, 4, 34);
    torii.fillRect(toriiX - 26, baseY - 40, 52, 4);
    torii.fillRect(toriiX - 22, baseY - 30, 44, 3);
    // 水面に映る影
    torii.fillStyle(0x2a1220, 0.24);
    torii.fillRect(toriiX - 20, baseY, 4, 14);
    torii.fillRect(toriiX + 16, baseY, 4, 14);
    container.add(torii);

    // 屋形船（提灯を灯してゆっくり横切る）
    const boat = this.add.container(-40, horizonY + (waterBottomY - horizonY) * 0.72);
    const hull = this.add.graphics();
    hull.fillStyle(0x1a1220, 0.9);
    hull.fillRoundedRect(-18, -3, 36, 7, 3);
    hull.fillStyle(0x241a2a, 0.9);
    hull.fillRect(-11, -11, 22, 8);
    boat.add(hull);
    for (let i = -1; i <= 1; i += 1) {
      const lamp = this.add
        .image(i * 9, -13, GLOW_TEXTURE_KEY)
        .setScale(0.34)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setTint(0xffc077);
      boat.add(lamp);
      this.tweens.add({
        targets: lamp,
        alpha: { from: 0.55, to: 1 },
        duration: 1400 + i * 260,
        yoyo: true,
        repeat: -1,
      });
    }
    container.add(boat);
    this.tweens.add({
      targets: boat,
      x: innerWidth + 40,
      duration: GAME_TUNING.scenery.boatDurationMs,
      repeat: -1,
      ease: 'Linear',
    });

    return container;
  }

  /**
   * 空の演出（花火・流れ星）の置き場。
   * 中身はタイマーで生成し、終わったら破棄する使い捨て。
   */
  private buildSkyFx(left: number, top: number, innerWidth: number): Phaser.GameObjects.Container {
    this.skyFx = this.add.container(left, top);
    this.skyFxBounds = { left, top, width: innerWidth, horizon: SCENERY_LAYOUT.horizonY };

    this.time.addEvent({
      delay: GAME_TUNING.scenery.fireworksIntervalMs,
      loop: true,
      callback: () => this.launchFirework(),
    });
    this.time.addEvent({
      delay: GAME_TUNING.scenery.shootingStarIntervalMs,
      loop: true,
      callback: () => this.launchShootingStar(),
    });

    return this.skyFx;
  }

  /** 夜以降だけ花火を上げる（進行のご褒美）。 */
  private launchFirework(): void {
    // 追悼・慰霊・聖域の景色の上では上げない。動きを減らす設定でも上げない。
    if (this.solemnScene || isMotionReduced(this.context.settings)) return;
    if (phaseIndex(this.phase) < phaseIndex(GAME_TUNING.scenery.fireworksFromPhase)) return;
    if (this.phase === 'dawn') return;

    const { width, horizon } = this.skyFxBounds;
    const x = width * (0.2 + Math.random() * 0.6);
    const y = horizon * (0.22 + Math.random() * 0.3);
    const hue = Math.random();
    const tint = hue < 0.34 ? 0xffb0c4 : hue < 0.67 ? 0xa8d8ff : 0xffe1a0;

    const flash = this.add
      .image(x, y, GLOW_TEXTURE_KEY)
      .setScale(0.4)
      .setTint(tint)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.skyFx.add(flash);
    this.tweens.add({
      targets: flash,
      scale: 2.4,
      alpha: 0,
      duration: 900,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });

    const petals = 12;
    for (let i = 0; i < petals; i += 1) {
      const angle = (Math.PI * 2 * i) / petals;
      const spark = this.add.circle(x, y, 1.8, tint, 1).setBlendMode(Phaser.BlendModes.ADD);
      this.skyFx.add(spark);
      const reach = 26 + Math.random() * 22;
      this.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * reach,
        y: y + Math.sin(angle) * reach + 10,
        alpha: 0,
        duration: 1100 + Math.random() * 400,
        ease: 'Quad.easeOut',
        onComplete: () => spark.destroy(),
      });
    }
  }

  /** 深夜だけ流れ星を流す。 */
  private launchShootingStar(): void {
    // 追悼・慰霊・聖域の景色の上では流さない。動きを減らす設定でも流さない。
    if (this.solemnScene || isMotionReduced(this.context.settings)) return;
    if (phaseIndex(this.phase) < phaseIndex(GAME_TUNING.scenery.shootingStarFromPhase)) return;
    if (this.phase === 'dawn') return;

    const { width, horizon } = this.skyFxBounds;
    const startX = width * (0.1 + Math.random() * 0.5);
    const startY = horizon * (0.1 + Math.random() * 0.25);
    const trail = this.add
      .rectangle(startX, startY, 22, 1.6, 0xfff4d6, 0.9)
      .setOrigin(1, 0.5)
      .setAngle(24)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.skyFx.add(trail);
    this.tweens.add({
      targets: trail,
      x: startX + 130,
      y: startY + 58,
      alpha: 0,
      duration: 780,
      ease: 'Sine.easeIn',
      onComplete: () => trail.destroy(),
    });
  }

  /** 屋台の炭から舞い上がる火の粉（近景の生活感）。 */
  private buildEmbers(
    left: number,
    top: number,
    innerWidth: number,
    innerHeight: number,
  ): Phaser.GameObjects.Container {
    this.embers = this.add.container(left, top);
    for (let i = 0; i < 10; i += 1) {
      const x = innerWidth * (0.12 + ((i * 17) % 76) / 100);
      const ember = this.add
        .circle(x, innerHeight - 24, i % 3 === 0 ? 1.8 : 1.2, 0xffb066, 0.9)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.embers.add(ember);
      this.tweens.add({
        targets: ember,
        y: innerHeight - 130 - ((i * 23) % 60),
        x: x + (i % 2 === 0 ? 18 : -18),
        alpha: { from: 0.85, to: 0 },
        duration: 3200 + ((i * 311) % 2400),
        repeat: -1,
        delay: (i * 421) % 3000,
        ease: 'Sine.easeOut',
      });
    }
    return this.embers;
  }

  /** 屋台の梁・提灯・のぼり。 */
  private buildStall(left: number, top: number, innerWidth: number): Phaser.GameObjects.Container {
    const container = this.add.container(left, top);
    const theme = this.context.stage.theme;

    // 梁（屋台の骨組み）
    const beam = this.add.graphics();
    beam.fillStyle(0x2b1a12, 0.92);
    beam.fillRect(0, 0, innerWidth, 9);
    beam.fillStyle(0xffffff, 0.05);
    beam.fillRect(0, 0, innerWidth, 2);
    container.add(beam);

    // のぼり（左右の縦幟）。中央の視界は塞がない
    const bannerColor = Phaser.Display.Color.HexStringToColor(theme.primaryColor).color;
    [10, innerWidth - 28].forEach((bx, index) => {
      const banner = this.add.graphics();
      banner.fillStyle(bannerColor, 0.34);
      banner.fillRoundedRect(0, 0, 18, 128, 4);
      banner.fillStyle(0xfff3dc, 0.16);
      for (let i = 0; i < 4; i += 1) banner.fillRect(5, 16 + i * 28, 8, 12);
      banner.setPosition(bx, 22);
      container.add(banner);
      this.tweens.add({
        targets: banner,
        angle: index === 0 ? { from: -1.6, to: 1.6 } : { from: 1.6, to: -1.6 },
        duration: 3800 + index * 600,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    });

    // 提灯の列
    const lanternCount = 5;
    const gap = innerWidth / (lanternCount + 1);
    for (let i = 0; i < lanternCount; i += 1) {
      const lx = gap * (i + 1);
      const lantern = this.add.container(lx, 9);

      const glow = this.add
        .image(0, 20, GLOW_TEXTURE_KEY)
        .setScale(1.18)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setTint(0xffb46a);
      this.lanternGlows.push(glow);

      const body = this.add.graphics();
      body.fillStyle(0x1c120c, 0.9);
      body.fillRect(-1, 0, 2, 8);
      body.fillStyle(0xf2e2c4, 1);
      body.fillEllipse(0, 20, 20, 26);
      body.fillStyle(0xc4341f, 0.82);
      body.fillEllipse(0, 20, 20, 26);
      body.fillStyle(0x2b1a12, 0.9);
      body.fillRect(-7, 8, 14, 3);
      body.fillRect(-7, 30, 14, 3);
      body.fillStyle(0x000000, 0.16);
      for (let s = -2; s <= 2; s += 1) body.fillRect(s * 4, 11, 1, 19);

      lantern.add([glow, body]);
      container.add(lantern);

      this.tweens.add({
        targets: lantern,
        angle: { from: -2.4, to: 2.4 },
        duration: 2600 + i * 320,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: i * 180,
      });
      // ゆらめきは scale で表現する（alpha は時間帯の制御に使うため）
      this.tweens.add({
        targets: glow,
        scale: { from: 1.02, to: 1.34 },
        duration: 900 + i * 220,
        yoyo: true,
        repeat: -1,
      });
    }

    return container;
  }

  /* --------------------------------- 近景 --------------------------------- */

  /** カウンターの木目と湯呑み（画面下端あたり）。 */
  private buildCounter(
    left: number,
    top: number,
    innerWidth: number,
    innerHeight: number,
  ): Phaser.GameObjects.Graphics {
    const graphics = this.add.graphics({ x: left, y: top });
    const baseY = innerHeight - 26;

    graphics.fillStyle(0x3a2418, 0.85);
    graphics.fillRect(0, baseY, innerWidth, 26);
    graphics.fillStyle(0x543421, 0.7);
    graphics.fillRect(0, baseY, innerWidth, 6);
    graphics.fillStyle(0xffffff, 0.045);
    for (let x = 6; x < innerWidth; x += 34) {
      graphics.fillRect(x, baseY + 8, 22, 2);
    }

    // 湯呑みと箸置き（シルエット）
    graphics.fillStyle(0x241610, 0.8);
    graphics.fillRoundedRect(innerWidth - 62, baseY - 16, 20, 18, 4);
    graphics.fillRoundedRect(30, baseY - 9, 34, 9, 4);

    return graphics;
  }

  private buildAmbientSteam(
    left: number,
    top: number,
    innerWidth: number,
    innerHeight: number,
  ): Phaser.GameObjects.Container {
    const container = this.add.container(left, top);
    for (let i = 0; i < 3; i += 1) {
      const x = innerWidth * (0.14 + i * 0.36);
      const steam = this.add
        .image(x, innerHeight - 30, STEAM_TEXTURE_KEY)
        .setOrigin(0.5, 1)
        .setDisplaySize(66, 150)
        .setAlpha(0);
      // setDisplaySize で決まった scale を基準に広がらせる（直接 0.7→1.25 にすると縮む）
      const baseScaleX = steam.scaleX;
      steam.setScale(baseScaleX * 0.7, steam.scaleY);
      container.add(steam);
      this.tweens.add({
        targets: steam,
        alpha: { from: 0, to: 0.09 },
        y: { from: innerHeight - 20, to: innerHeight - 96 },
        scaleX: { from: baseScaleX * 0.7, to: baseScaleX * 1.25 },
        duration: 5200 + i * 900,
        ease: 'Sine.easeOut',
        repeat: -1,
        delay: i * 1700,
        hold: 200,
        yoyo: true,
      });
    }
    return container;
  }

  private buildVignette(
    left: number,
    top: number,
    innerWidth: number,
    innerHeight: number,
  ): Phaser.GameObjects.Image {
    return this.add
      .image(left, top, VIGNETTE_TEXTURE_KEY)
      .setOrigin(0, 0)
      .setDisplaySize(innerWidth, innerHeight);
  }

  /* ------------------------------ 背景ツアー ------------------------------ */

  private buildTourLayer(width: number, height: number): void {
    const tour = this.context.stage.scenery?.tour;
    const tourPhotos = this.context.tourPhotos;
    if (!tour || tourPhotos.size === 0) return;

    this.tourAvailable = true;
    this.tourLayer = this.add.container(0, 0).setDepth(-3);

    tour.scenes.forEach((scene) => {
      if (!tourPhotos.has(scene.id)) return;
      const key = tourPhotoKey(scene.id);
      const scale = this.coverScale(key, width, height);
      const image = this.add
        .image(width / 2, height / 2, key)
        .setOrigin(0.5, 0.5)
        .setScale(scale)
        .setAlpha(0);
      this.tourImages.set(scene.id, image);
      this.tourLayer!.add(image);

      // マーカーも同じ container に入れて拡大縮小に追従させる
      scene.hotspots.forEach((hotspot) => {
        const marker = this.buildTourMarker(hotspot, width, height);
        this.tourMarkers.set(hotspot.id, marker);
        this.tourLayer!.add(marker);
      });
    });

    // 下端に向けて暗くなるグラデーション（ビネット流用）
    const vignette = this.add
      .image(0, 0, VIGNETTE_TEXTURE_KEY)
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setAlpha(0.55);
    this.tourLayer.add(vignette);

    // 看板は画像 container より上に置く
    this.tourSignboard = this.buildSignboard(width);
    this.tourSignboard.setDepth(2);
    this.tourSignboard.setAlpha(0);

    this.switchTourScene(tour.scenes[0]?.id, true);
  }

  private buildTourMarker(
    hotspot: TourHotspot,
    width: number,
    height: number,
  ): Phaser.GameObjects.Container {
    // 描画も当たり判定も tourHotspotWorldPos に一本化する（別々に作ると必ずずれる）
    const pos = tourHotspotWorldPos(
      hotspot.x,
      hotspot.y,
      width,
      height,
      GAME_TUNING.world.bowl.rimY,
      GAME_TUNING.scenery.tour.hotspotRimMarginPx,
    );
    const container = this.add.container(pos.x, pos.y);
    container.setVisible(false);

    // 外輪（ゆっくり明滅）
    const ring = this.add
      .image(0, 0, GLOW_TEXTURE_KEY)
      .setScale(0.55)
      .setTint(0xfff0c8)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0.7);
    container.add(ring);

    // 中央のきらっ（小さな星）
    const core = this.add
      .image(0, 0, GLOW_TEXTURE_KEY)
      .setScale(0.22)
      .setTint(0xffffff)
      .setBlendMode(Phaser.BlendModes.ADD);
    container.add(core);

    // 明滅アニメーション
    this.tweens.add({
      targets: ring,
      alpha: { from: 0.35, to: 0.9 },
      scale: { from: 0.5, to: 0.65 },
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: core,
      alpha: { from: 0.75, to: 1 },
      scale: { from: 0.2, to: 0.26 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return container;
  }

  private buildSignboard(width: number): Phaser.GameObjects.Container {
    const container = this.add.container(width / 2, 52);

    const bg = this.add.graphics();
    bg.fillStyle(0x2b1a17, 0.82);
    bg.fillRoundedRect(-130, -22, 260, 44, 8);
    bg.lineStyle(2, 0xf2c14e, 0.65);
    bg.strokeRoundedRect(-130, -22, 260, 44, 8);
    container.add(bg);

    const nameText = this.add
      .text(0, -6, '', {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '17px',
        fontStyle: 'bold',
        color: '#fff8ee',
      })
      .setOrigin(0.5);
    container.add(nameText);

    const areaText = this.add
      .text(0, 12, '', {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '11px',
        color: '#e8d8c0',
      })
      .setOrigin(0.5);
    container.add(areaText);

    (container as unknown as { nameText: Phaser.GameObjects.Text }).nameText = nameText;
    (container as unknown as { areaText: Phaser.GameObjects.Text }).areaText = areaText;

    return container;
  }

  /** ツアーが有効かどうか（写真が1枚以上読み込めているか）。 */
  isTourActive(): boolean {
    return this.tourAvailable && this.tourLayer !== undefined;
  }

  /** 現在表示中のツアー景色を返す。 */
  getCurrentScene(): TourScene | null {
    const tour = this.context.stage.scenery?.tour;
    if (!tour || !this.currentTourSceneId) return null;
    return tour.scenes.find((scene) => scene.id === this.currentTourSceneId) ?? null;
  }

  /**
   * ツアー時間を更新する。
   * GameScene の update から PLAYING 中の経過時間を渡して呼ぶ。
   */
  updateTour(elapsedMs: number): void {
    if (!this.isTourActive()) return;

    // updateTour はプレイ中しか呼ばれないので、ここが「遊び始めた瞬間」。
    // 開始直後だと盤面に目が行っているので、少し置いてからヒントを出す。
    if (!this.tourHintScheduled) {
      this.tourHintScheduled = true;
      this.time.delayedCall(1500, () => this.maybeShowTourHint());
    }

    const tour = this.context.stage.scenery?.tour;
    if (!tour) return;
    const tourConfig = GAME_TUNING.scenery.tour;

    const previousIndex = sceneIndexAt(
      this.tourElapsedMs,
      tourConfig.secondsPerScene,
      tour.scenes.length,
      tourConfig.loop,
    );
    const state = computeTourState(
      elapsedMs,
      tourConfig.secondsPerScene,
      tour.scenes.length,
      tourConfig.loop,
      previousIndex,
    );
    this.tourElapsedMs = elapsedMs;

    const scene = tour.scenes[state.index];
    if (scene && scene.id !== this.currentTourSceneId) {
      this.switchTourScene(scene.id);
    }

    if (state.justChanged) {
      this.showSignboard(scene);
    }
  }

  private switchTourScene(sceneId: string | undefined, immediate = false): void {
    if (!sceneId || sceneId === this.currentTourSceneId) return;

    const previousId = this.currentTourSceneId;
    this.currentTourSceneId = sceneId;

    const crossfadeMs = immediate ? 0 : GAME_TUNING.scenery.tour.crossfadeMs;

    this.tourImages.forEach((image, id) => {
      const alpha = id === sceneId ? 1 : 0;
      if (immediate) {
        image.setAlpha(alpha);
      } else {
        this.tweens.add({ targets: image, alpha, duration: crossfadeMs, ease: 'Sine.easeInOut' });
      }
    });

    const currentScene = this.getCurrentScene();
    this.tourMarkers.forEach((marker, hotspotId) => {
      const belongsToCurrent = currentScene?.hotspots.some((h) => h.id === hotspotId) ?? false;
      marker.setVisible(belongsToCurrent);
      if (belongsToCurrent) {
        const hotspot = currentScene?.hotspots.find((h) => h.id === hotspotId);
        if (hotspot) {
          // 未発見は「押せる」とわかるようはっきり光らせ、発見済みは控えめにする
          const discovered = this.context.storage.discoveredTipIds.includes(hotspotId);
          marker.setAlpha(discovered ? 0.3 : 1);
          marker.setScale(discovered ? 0.9 : 1.15);
        }
      }
    });

    this.applySolemn(currentScene?.solemn === true);
    this.context.bus.emit('tour:scene', { scene: currentScene });

    // 切り替わったら看板を表示
    if (currentScene) {
      this.showSignboard(currentScene);
    }

    // 手続き生成の空をツアー背景に合わせて暗くする（夜→深夜相当）
    if (previousId === undefined) {
      this.applyPhase('night', immediate);
    }
  }

  /**
   * 「静かに扱うべき景色」かどうかを切り替える。
   *
   * 空に残っている花火や流れ星は、その場で消さずに自然に消える（急に消えるほうが目立つ）。
   * 新しく上がらなくなるだけで十分。
   */
  private applySolemn(solemn: boolean): void {
    if (solemn === this.solemnScene) return;
    this.solemnScene = solemn;
    this.context.bus.emit('scene:solemn', { solemn });
  }

  private showSignboard(scene: TourScene): void {
    if (!this.tourSignboard) return;
    const nameText = (this.tourSignboard as unknown as { nameText?: Phaser.GameObjects.Text })
      .nameText;
    const areaText = (this.tourSignboard as unknown as { areaText?: Phaser.GameObjects.Text })
      .areaText;
    if (nameText) nameText.text = scene.name;
    if (areaText) areaText.text = scene.area;

    this.tourSignboard.setAlpha(0);
    this.tweens.killTweensOf(this.tourSignboard);
    this.tweens.add({
      targets: this.tourSignboard,
      alpha: { from: 0, to: 1 },
      duration: 400,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: this.tourSignboard,
          alpha: 0,
          delay: GAME_TUNING.scenery.tour.signboardMs,
          duration: 600,
          ease: 'Sine.easeIn',
        });
      },
    });
  }

  /**
   * 指定座標にヒットするホットスポットを返す。
   * 画面上の位置が器の縁より下のものは反応しない。
   */
  hitTestHotspot(pointerX: number, pointerY: number): TourHotspot | null {
    const scene = this.getCurrentScene();
    if (!scene) return null;

    const { width, height } = GAME_TUNING.world;
    const rimY = GAME_TUNING.world.bowl.rimY;
    const radius = GAME_TUNING.scenery.tour.hotspotHitRadius;

    return findHitHotspot(
      pointerX,
      pointerY,
      scene,
      width,
      height,
      rimY,
      radius,
      GAME_TUNING.scenery.tour.hotspotRimMarginPx,
    );
  }

  /**
   * 初見のプレイヤーに、背景の光が押せることを一度だけ伝える。
   *
   * 豆知識を1つでも開いたことがある人には出さない。
   * 背景ツアーは本作の売りの一つだが、光に気づかないと丸ごと体験されずに終わるため。
   */
  private maybeShowTourHint(): void {
    // 案内は盤面の外（「✨ 豆知識」ボタンの下の吹き出し＝TourChip）に移した。
    // 盤面の中に出すと、落とす料理と箸に重なっていた。
    if (!SHOW_CANVAS_TOUR_HINT) return;
    if (this.tourHintShown || !this.isTourActive()) return;
    if (this.context.storage.discoveredTipIds.length > 0) return;

    const scene = this.getCurrentScene();
    if (!scene) return;

    const { width, height, bowl } = GAME_TUNING.world;
    // 位置は tourHotspotWorldPos が縁より上に収めてくれるので、先頭を使えばよい
    const [target] = scene.hotspots.map((hotspot) =>
      tourHotspotWorldPos(
        hotspot.x,
        hotspot.y,
        width,
        height,
        bowl.rimY,
        GAME_TUNING.scenery.tour.hotspotRimMarginPx,
      ),
    );
    if (!target) return;

    this.tourHintShown = true;
    this.tourHint = this.buildTourHint(target.x, target.y, width);

    this.tweens.add({
      targets: this.tourHint,
      alpha: { from: 0, to: 1 },
      y: { from: target.y - 30, to: target.y - 38 },
      duration: 600,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.time.delayedCall(5200, () => this.hideTourHint());
      },
    });
  }

  /** ヒントの吹き出し（マーカーを指す小さな札）。 */
  private buildTourHint(x: number, y: number, width: number): Phaser.GameObjects.Container {
    const text = this.add
      .text(0, 0, 'この光は豆知識。左上のボタンから開けます', {
        fontFamily: '"Hiragino Sans", "Noto Sans JP", sans-serif',
        fontSize: '12px',
        color: '#2b1a17',
      })
      .setOrigin(0.5);

    const boxWidth = Math.round(text.width + 20);
    const boxHeight = 24;

    // 画面からはみ出さない位置に寄せ、尻尾だけマーカー側へずらす
    const half = boxWidth / 2 + 6;
    const centerX = Phaser.Math.Clamp(x, half, width - half);
    const tailX = Phaser.Math.Clamp(x - centerX, -boxWidth / 2 + 10, boxWidth / 2 - 10);

    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.3);
    bg.fillRoundedRect(-boxWidth / 2 + 2, -boxHeight / 2 + 3, boxWidth, boxHeight, 6);
    bg.fillStyle(0xf2c14e, 0.97);
    bg.fillRoundedRect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight, 6);
    bg.fillTriangle(tailX - 5, boxHeight / 2, tailX + 5, boxHeight / 2, tailX, boxHeight / 2 + 7);

    return this.add.container(centerX, y - 38, [bg, text]).setDepth(3).setAlpha(0);
  }

  private hideTourHint(): void {
    const hint = this.tourHint;
    if (!hint) return;
    this.tourHint = undefined;
    this.tweens.add({
      targets: hint,
      alpha: 0,
      duration: 400,
      onComplete: () => hint.destroy(),
    });
  }

  /** リスタート時に表示中の景色を再計算する。累積時間はリセットしない。 */
  resetTour(): void {
    const tour = this.context.stage.scenery?.tour;
    if (!tour) return;
    const tourConfig = GAME_TUNING.scenery.tour;
    const state = computeTourState(
      this.tourElapsedMs,
      tourConfig.secondsPerScene,
      tour.scenes.length,
      tourConfig.loop,
      -1,
    );
    this.switchTourScene(tour.scenes[state.index]?.id, true);
  }

  /** 累積時間を 0 に戻し、最初の景色から始め直す（設定からのリセット用）。 */
  resetTourProgress(): void {
    this.tourElapsedMs = 0;
    const tour = this.context.stage.scenery?.tour;
    if (tour) {
      this.switchTourScene(tour.scenes[0]?.id, true);
    }
  }

  private applyTourVisibility(): void {
    const active = this.isTourActive();
    const showDecor = active ? GAME_TUNING.scenery.tour.showYataiDecor : true;

    this.skyContainer.setVisible(!active);
    this.vignette.setVisible(!active);
    this.stall.setVisible(showDecor);
    this.counter.setVisible(showDecor);
    this.ambientSteam.setVisible(showDecor);

    if (active) {
      // 手続き生成の遠景は写真背景に任せる
      this.farView.setVisible(false);
      this.bay.setVisible(false);
      this.sparkles.setVisible(false);
      this.stars.setVisible(false);
    }
  }

  /* ------------------------------ 時間帯の切替 ------------------------------ */

  private applyPhase(phase: ScenePhase, immediate = false): void {
    if (!immediate && phase === this.phase) return;
    this.phase = phase;

    const duration = immediate ? 0 : GAME_TUNING.scenery.phaseFadeMs;
    this.skies.forEach((image, key) => {
      const alpha = key === phase ? 1 : 0;
      if (immediate) {
        image.setAlpha(alpha);
        return;
      }
      this.tweens.add({ targets: image, alpha, duration, ease: 'Sine.easeInOut' });
    });

    /*
     * 実写写真にはすでに街・水面・星が写っているので、
     * 手続き生成の遠景（ビル影・大鳥居・水面のきらめき・星）は伏せる。
     * 屋台・提灯・カウンター・湯気などの前景はそのまま残して奥行きを作る。
     */
    const photoMode = this.photoPhases.has(phase);
    const tourActive = this.isTourActive();
    this.farView.setVisible(!photoMode && !tourActive);
    this.bay.setVisible(!photoMode && !tourActive);
    this.sparkles.setVisible(!photoMode && !tourActive);
    this.stars.setVisible(!photoMode && !tourActive);

    const intensity = lightIntensityOf(phase);
    const starAlpha = Math.max(0, intensity - 0.2);
    const lanternAlpha = 0.3 + intensity * 0.7;
    const sparkleAlpha = 0.4 + intensity * 0.6;
    const emberAlpha = 0.45 + intensity * 0.55;
    if (immediate) {
      this.stars.setAlpha(starAlpha);
      this.cityLights.setAlpha(intensity);
      this.lanternGlows.forEach((glow) => glow.setAlpha(lanternAlpha));
      this.sparkles.setAlpha(sparkleAlpha);
      this.embers.setAlpha(emberAlpha);
      return;
    }
    this.tweens.add({ targets: this.stars, alpha: starAlpha, duration });
    this.tweens.add({ targets: this.cityLights, alpha: intensity, duration });
    this.tweens.add({ targets: this.lanternGlows, alpha: lanternAlpha, duration });
    this.tweens.add({ targets: this.sparkles, alpha: sparkleAlpha, duration });
    this.tweens.add({ targets: this.embers, alpha: emberAlpha, duration });
  }
}
